import React from 'react';
import {
	FaUserAlt,
	FaSignInAlt,
	FaSignOutAlt,
	FaReceipt,
	FaCoins,
	FaClock,
	FaRegCalendarAlt,
	FaTrash,
	FaInfoCircle,
	FaRegArrowAltCircleRight,
	FaEyeSlash,
	FaRss,
	FaChevronRight,
} from 'react-icons/fa';
import styled from 'styled-components';
import { Switch } from 'playup-web-core';
import { noop } from 'lodash';

import MenuLink from './MenuLink';
import { ClickAnywhereElseToCloseCover } from './components';
import NavWrapper from './NavWrapper';
import appConfig from '../../../appConfig';
import Footer from '../../Footer/Footer';

interface MenuNavProps {
	isLoggedIn: boolean;
	isSmallScreen: boolean;
	isVerified: boolean;
	hideBalance: boolean;
	logout: () => void;
	toggleBalance: () => void;
	toggleMenu: () => void;
}

const MenuNav = ({
	hideBalance,
	isLoggedIn,
	isSmallScreen,
	isVerified,
	logout,
	toggleMenu,
	toggleBalance,
}: MenuNavProps) => {
	const navEntries: Array<{
		url: string;
		content: React.ReactNode;
		icon: React.ReactNode;
		rightContent?: React.ReactNode;
		onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
	}> = React.useMemo(() => {
		if (!isLoggedIn)
			return [
				{ url: '?modal=login', content: 'Login', icon: <FaSignInAlt /> },
				{ url: '?modal=signup', content: 'Signup', icon: <FaSignOutAlt /> },
			];

		return [
			{
				url: '/account/profile',
				content: <>Profile {!isVerified && <VerifiedStatusPill>Unverified</VerifiedStatusPill>}</>,
				icon: (
					<>
						<FaUserAlt />
						{!isVerified && <NotificationCircle isSmall={isSmallScreen} />}
					</>
				),
			},
			{ url: '?modal=deposit', content: 'Deposit', icon: <FaSignInAlt /> },
			{
				url: '/account/withdraw',
				content: 'Withdraw',
				icon: (
					<>
						<FaSignOutAlt /> {!isVerified && <WarningInfoCircle />}
					</>
				),
			},
			{ url: '/account/transactions', content: 'Cash Transactions', icon: <FaReceipt /> },
			{ url: '/account/limits', content: 'Limits', icon: <FaCoins /> },
			{ url: '/account/take-a-break', content: 'Take a break', icon: <FaClock /> },
			{ url: '/account/self-exclusion', content: 'Self Exclusion', icon: <FaRegCalendarAlt /> },
			{ url: '/account/close-account', content: 'Close Account', icon: <FaTrash /> },
			{ url: '?modal=whatsnew', content: "What's New", icon: <FaRss /> },
			{
				url: '#',
				content: 'Hide balance',
				onClick: (e) => {
					e.stopPropagation();
					toggleBalance();
				},
				icon: <FaEyeSlash />,
				rightContent: <Switch onChange={noop} name="hidebalance" active={hideBalance} />,
			},
			{
				url: '#',
				onClick: logout,
				content: 'Logout',
				icon: <FaRegArrowAltCircleRight />,
			},
		];
	}, [isLoggedIn, hideBalance, isVerified, isSmallScreen, logout, toggleBalance]);

	return (
		<>
			{!isSmallScreen && <ClickAnywhereElseToCloseCover onClick={toggleMenu} />}
			<NavWrapper isSmallScreen={isSmallScreen}>
				{navEntries.map(({ url, content, icon, onClick = toggleMenu, rightContent }, i) => (
					<MenuLink key={i} to={url} isSmallScreen={isSmallScreen} onClick={onClick}>
						<MenuIconWrapper isSmallScreen={isSmallScreen}>{icon}</MenuIconWrapper>
						{content}
						<RightContentWrapper>
							{rightContent && rightContent}
							{!rightContent && isSmallScreen && <FaChevronRight />}
						</RightContentWrapper>
					</MenuLink>
				))}
				{isSmallScreen && <Footer hideRG />}

				{isSmallScreen && appConfig.portal.appVersion && (
					<div className="md:_self-end _text-xs _mt-2 _mr-2 md:_mr-0 lg:_mt-0 _text-right">
						Version {appConfig.portal.appVersion}
					</div>
				)}
			</NavWrapper>
		</>
	);
};

export default MenuNav;

const RightContentWrapper = styled.div`
	flex-grow: 1;
	justify-content: flex-end;
	display: flex;
	align-items: center;
`;

const MenuIconWrapper = styled.div<{ isSmallScreen: boolean }>`
	position: relative;
	color: ${({ theme }) => theme.colors.greyR};
	margin-right: ${({ isSmallScreen }) => (isSmallScreen ? '20px' : '25px')};
`;

const VerifiedStatusPill = styled.div`
	background-color: #cd3024;
	padding: 1px 10px;
	border-radius: 20px;
	margin-left: 10px;
	height: 14px;
	font-size: 11px;
	font-weight: 600;
	display: flex;
	align-items: center;
	justify-items: center;
`;

const NotificationCircle = styled.div<{ isSmall: boolean }>`
	position: absolute;
	width: 10px;
	height: 10px;
	border-radius: 100%;
	background-color: ${(props) => props.theme.colors.danger};
	right: 0;
	top: -5px;
	border-width: 1px;
	border-color: ${(props) => props.theme.colors.darkGreyR};
`;

const WarningInfoCircle = styled(FaInfoCircle)`
	position: absolute;
	width: 10px;
	height: 10px;
	color: ${({ theme }) => theme.colors.warning};
	right: 0;
	top: -5px;
`;
